import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import _ from "lodash/fp.js";
import { useState, Children, cloneElement, useRef, useEffect } from "react";
import { useUniqueId } from "./hooks/useUniqueId.js";
import { containsOnlyUnlabelledIcon } from "./internal/a11y-utils.js";
import { useBoundingRects, computePopupPosition } from "./internal/popup-utils.js";
import { PopupPortal } from "./internal/PopupPortal.js";
import { useThemeFromContext } from "./theme.js";
const styles = {
  tooltip: {
    position: "fixed",
    top: 0,
    left: 0,
    maxWidth: 400,
    padding: "1rem",
    borderRadius: 4,
    background: "#FFFFFF",
    boxShadow: "0 1px 3px 2px rgba(0,0,0,0.3)",
    lineHeight: 1.5,
    pointerEvents: "none"
  },
  notch: {
    position: "absolute",
    width: 17,
    height: 8,
    marginLeft: -8,
    marginRight: -8,
    marginTop: -8,
    transformOrigin: "bottom",
    filter: "drop-shadow(0px -2px 1px rgba(0,0,0,0.3))",
    fill: "#FFFFFF"
  }
};
const Tooltip = (props) => {
  const { children, delay, id, side = "bottom", targetId } = props;
  const theme = useThemeFromContext();
  const [shouldRender, setShouldRender] = useState(!delay);
  const elementRef = useRef(null);
  const [targetBounds, tooltipBounds, viewportBounds] = useBoundingRects([
    { id: targetId },
    { ref: elementRef },
    { viewport: true }
  ]);
  useEffect(() => {
    if (delay) {
      const renderTimeout = setTimeout(() => {
        setShouldRender(true);
      }, delay);
      return () => clearTimeout(renderTimeout);
    }
  }, [delay]);
  const { position, side: finalSide } = computePopupPosition({
    elementSize: tooltipBounds,
    gap: 10,
    targetPosition: targetBounds,
    preferredSide: side,
    viewportSize: viewportBounds
  });
  const getNotchPositionStyle = () => {
    const left = _.clamp(12, tooltipBounds.width - 12, (targetBounds.left + targetBounds.right) / 2 - position.left);
    const top = _.clamp(12, tooltipBounds.height - 12, (targetBounds.top + targetBounds.bottom) / 2 - position.top);
    switch (finalSide) {
      case "top":
        return { bottom: 1, left, transform: "rotate(180deg)" };
      case "right":
        return { left: 1, top, transform: "rotate(270deg)" };
      case "bottom":
        return { top: 1, left };
      case "left":
        return { right: 1, top, transform: "rotate(90deg)" };
      default:
        throw new Error("Invalid side");
    }
  };
  return jsx(PopupPortal, { children: jsxs("div", { id, role: "tooltip", ref: elementRef, style: {
    ...styles.tooltip,
    display: shouldRender ? void 0 : "none",
    transform: `translate(${position.left}px, ${position.top}px)`,
    color: theme.colors.dark(),
    borderColor: theme.colors.secondary()
  }, children: [children, jsx("svg", { viewBox: "0 0 2 1", style: { ...styles.notch, ...getNotchPositionStyle() }, children: jsx("path", { d: "M0,1l1,-1l1,1Z" }) })] }) });
};
const TooltipTrigger = (props) => {
  const { children, content, delay, side, useTooltipAsLabel } = props;
  const hasTooltipContent = !!content;
  const [isOpen, setIsOpen] = useState(false);
  const id = useUniqueId();
  const tooltipId = useUniqueId();
  const descriptionId = useUniqueId();
  const child = Children.only(children);
  const childId = child.props.id || id;
  const useAsLabel = _.isNil(useTooltipAsLabel) ? containsOnlyUnlabelledIcon(child.props) : useTooltipAsLabel;
  return jsxs(Fragment, { children: [cloneElement(child, {
    id: childId,
    "aria-labelledby": hasTooltipContent && useAsLabel ? descriptionId : void 0,
    "aria-describedby": hasTooltipContent && !useAsLabel ? descriptionId : void 0,
    onMouseEnter: (...args) => {
      var _a, _b;
      (_b = (_a = child.props).onMouseEnter) == null ? void 0 : _b.call(_a, ...args);
      setIsOpen(true);
    },
    onMouseLeave: (...args) => {
      var _a, _b;
      (_b = (_a = child.props).onMouseLeave) == null ? void 0 : _b.call(_a, ...args);
      setIsOpen(false);
    },
    onFocus: (...args) => {
      var _a, _b;
      (_b = (_a = child.props).onFocus) == null ? void 0 : _b.call(_a, ...args);
      setIsOpen(true);
    },
    onBlur: (...args) => {
      var _a, _b;
      (_b = (_a = child.props).onBlur) == null ? void 0 : _b.call(_a, ...args);
      setIsOpen(false);
    }
  }), hasTooltipContent && jsxs(Fragment, { children: [isOpen && jsx(Tooltip, { delay, id: tooltipId, side, targetId: childId, children: content }), jsx("div", { id: descriptionId, style: { display: "none" }, children: content })] })] });
};
export {
  TooltipTrigger
};
